/**
 * ContentProtection — wrapper that discourages (but cannot fully prevent)
 * screenshots and content copying.
 *
 * Measures applied:
 * 1. CSS `user-select: none` + `-webkit-touch-callout: none`
 * 2. Block keyboard shortcuts: PrintScreen, Ctrl+P, Ctrl+Shift+I
 * 3. Blur content when the window loses focus (visibilitychange)
 * 4. Semi-transparent watermark with the current user's name + timestamp
 * 5. Context-menu disabled on the protected area
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useProfile } from '@/api/profile';

type Props = {
  children: ReactNode;
  /** Show the watermark overlay. Default: true */
  watermark?: boolean;
};

export function ContentProtection({ children, watermark = true }: Props) {
  const { data: profile } = useProfile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);

  // Build watermark text
  const watermarkText = profile
    ? `${profile.first_name} ${profile.last_name} — ${profile.email}`
    : '';

  // --- Block keyboard shortcuts ---
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // PrintScreen
    if (e.key === 'PrintScreen') {
      e.preventDefault();
      return;
    }
    // Ctrl+P (print)
    if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
      e.preventDefault();
      return;
    }
    // Ctrl+Shift+I or F12 (devtools)
    if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') || e.key === 'F12') {
      e.preventDefault();
      return;
    }
    // Ctrl+Shift+S (screenshot in some browsers)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
      e.preventDefault();
      return;
    }
  }, []);

  // --- Blur when tab loses focus ---
  const handleVisibility = useCallback(() => {
    setHidden(document.hidden);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [handleKeyDown, handleVisibility]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div ref={containerRef} onContextMenu={handleContextMenu} className="content-protected">
      {/* Protected content */}
      {children}

      {/* Blur overlay when tab is hidden — fixed to cover the entire viewport */}
      {hidden && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-xl">
          <p className="text-sm font-medium text-muted-foreground">
            Contenido oculto — regresa a esta pestaña para continuar
          </p>
        </div>
      )}

      {/* Watermark overlay — fixed to cover the entire viewport including fixed children */}
      {watermark && watermarkText && (
        <div
          className="pointer-events-none fixed inset-0 z-[60] overflow-hidden"
          aria-hidden="true"
        >
          <div className="watermark-grid h-full w-full">
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="watermark-text">
                {watermarkText}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
